import {
  Badge,
  Flex,
  Icon,
  type IconType,
  Spinner,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import {
  V2BookConnectionState,
  type V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import { useTranslation } from 'react-i18next';

interface IConnectionStatusTagProps {
  connections: V2BookConnectionDescriptor[];
  status?: V2BookConnectionState;
  style?: 'badge' | 'icon';
}

interface IConnectionStatusConfig {
  color: string;
  icon: IconType | null;
  label: string;
  isLoading?: boolean;
}

export function ConnectionStatusTag({
  connections,
  status,
  style = 'badge',
}: IConnectionStatusTagProps) {
  const { t: tIntegrations } = useTranslation('integrations');

  if (!status) return null;

  const config: Record<V2BookConnectionState, IConnectionStatusConfig> = {
    [V2BookConnectionState.BOOK_CONNECTION_STATE_PENDING]: {
      color: 'text',
      icon: null,
      label: tIntegrations('integrations.connectionStatus.pending'),
      isLoading: true,
    },
    [V2BookConnectionState.BOOK_CONNECTION_STATE_READY]: {
      color: 'text-success',
      icon: 'Check',
      label: tIntegrations('integrations.connectionStatus.ready'),
    },
    [V2BookConnectionState.BOOK_CONNECTION_STATE_FAILED]: {
      color: 'text-destructive',
      icon: 'X',
      label: tIntegrations('integrations.connectionStatus.failed'),
    },
    [V2BookConnectionState.BOOK_CONNECTION_STATE_CONDITIONS_NOT_MET]: {
      color: 'text-warning',
      icon: 'AlertCircle',
      label: tIntegrations('integrations.connectionStatus.conditionsNotMet'),
    },
    [V2BookConnectionState.BOOK_CONNECTION_STATE_UNKNOWN]: {
      color: 'text-muted',
      icon: 'HelpCircle',
      label: tIntegrations('integrations.connectionStatus.unknown'),
    },
    // Scaled to zero after a period of inactivity. Not an error — the
    // connection is intact and can be reactivated on demand.
    [V2BookConnectionState.BOOK_CONNECTION_STATE_INACTIVE]: {
      color: 'text-muted-foreground',
      icon: 'Moon',
      label: tIntegrations('integrations.connectionStatus.inactive'),
    },
  };

  const statusConfig = config[status];
  if (!statusConfig) return null;

  const { color, icon, label, isLoading } = statusConfig;

  const testId = `connection-status-tag-${connections[0]?.connection_id ?? 'unknown'}`;

  const isIconStyle = style === 'icon';

  const connectionStatusContent = (
    <Flex data-testid={testId} align="center" gap={isIconStyle ? 1.5 : 2}>
      {isLoading ? (
        // `Spinner` has no `xs`, so the pending state keeps `sm` in both.
        <Spinner size="sm" className={color} />
      ) : (
        <Icon
          type={icon as IconType}
          size={isIconStyle ? 'xs' : 'sm'}
          className={color}
        />
      )}
      {/* The icon style has no `Badge` around it to size its contents, so the
          bare label would inherit the ancestor's font size and outweigh the
          xSmall/small text it sits beside. */}
      {isIconStyle ? <Text level="xSmall">{label}</Text> : label}
    </Flex>
  );

  const tag =
    style === 'badge' ? (
      <Badge variant="outline">{connectionStatusContent}</Badge>
    ) : (
      connectionStatusContent
    );

  // "Inactive" is the one state that reads alarming but isn't: automations keep
  // running and restart the connection on trigger. Explain that inline rather
  // than through the diagnostics popover, which is feature-gated and would leave
  // most users staring at an unexplained badge. `shouldShowHealthDetails`
  // excludes INACTIVE so the two never wrap the same tag.
  if (status === V2BookConnectionState.BOOK_CONNECTION_STATE_INACTIVE) {
    return (
      <Tooltip>
        {/* Span trigger: the tag isn't focusable, so the tooltip needs a host
            element that reliably receives hover. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">{tag}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {tIntegrations('integrations.connectionStatus.inactiveTooltip')}
        </TooltipContent>
      </Tooltip>
    );
  }

  return tag;
}
